import { useEffect, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import AppCard from "@/components/AppCard";
import { LayoutGrid } from "lucide-react";

export default function Home() {
  const [apps, setApps] = useState([]);
  const [settings, setSettings] = useState({ site_title: "TV App Catalog", logo_path: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = () => {
      api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
      api.get("/apps").then((r) => setApps(r.data)).catch(() => {}).finally(() => setLoading(false));
    };
    load();
    // Refresh when user comes back to the tab or window regains focus
    const onFocus = () => load();
    const onVisibility = () => { if (!document.hidden) load(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const logo = fileUrl(settings.logo_path);

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-x-hidden">
      {/* Grain overlay */}
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.035] z-50 mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />

      {/* Ambient glow */}
      <div className="pointer-events-none fixed -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-[#E2FF31]/5 blur-[120px]" />
      <div className="pointer-events-none fixed -bottom-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#31A2FF]/5 blur-[120px]" />

      {/* Header */}
      <header className="relative z-10 px-12 lg:px-20 pt-0 pb-2">
        <div className="flex flex-col items-center text-center" data-testid="site-header">
          {logo ? (
            <img
              src={logo}
              alt="Logo"
              className="w-[26rem] h-[26rem] lg:w-[38rem] lg:h-[38rem] object-contain"
              data-testid="site-logo"
            />
          ) : (
            <div className="w-[26rem] h-[26rem] lg:w-[38rem] lg:h-[38rem] rounded-xl bg-[#E2FF31] flex items-center justify-center">
              <LayoutGrid className="w-52 h-52 lg:w-72 lg:h-72 text-black" strokeWidth={2.5} />
            </div>
          )}
          <h1
            className="font-heading text-4xl lg:text-6xl xl:text-7xl font-black tracking-tighter leading-none -mt-32 lg:-mt-48"
            data-testid="site-title"
          >
            {settings.site_title}
          </h1>
        </div>
      </header>

      {/* Divider */}
      <div className="px-12 lg:px-20">
        <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      </div>

      {/* Catalog */}
      <main className="relative z-10 px-12 lg:px-20 py-12 lg:py-16">
        {loading ? (
          <div className="text-white/40 text-2xl" data-testid="loading-state">Loading catalog…</div>
        ) : apps.length === 0 ? (
          <div
            className="flex flex-col items-start gap-4 py-24 border border-dashed border-white/10 rounded-3xl px-12"
            data-testid="empty-state"
          >
            <span className="text-sm uppercase tracking-[0.3em] text-white/40">No apps yet</span>
            <p className="font-heading text-4xl lg:text-5xl font-bold tracking-tight max-w-2xl">
              This catalog is empty. Check back soon.
            </p>
          </div>
        ) : (
          <>
            <div className="mb-10 flex items-baseline justify-between">
              <span className="text-sm lg:text-base uppercase tracking-[0.3em] text-white/40">
                {apps.length} {apps.length === 1 ? "app" : "apps"} available
              </span>
            </div>
            <div
              className="flex flex-col gap-6 lg:gap-8"
              data-testid="apps-grid"
            >
              {apps.map((app, idx) => (
                <AppCard key={app.id} app={app} index={idx} />
              ))}
            </div>
          </>
        )}
      </main>

      <footer className="relative z-10 px-12 lg:px-20 py-12 border-t border-white/5 mt-12">
        <p className="text-white/30 text-sm uppercase tracking-[0.3em]">
          Optimized for TV browsers • Use arrow keys to navigate
        </p>
      </footer>
    </div>
  );
}
